import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, DollarSign, Users, Trophy, Gift, Zap, CheckCircle } from "lucide-react";

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (user) {
    navigate("/dashboard", { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen mesh-bg" data-testid="landing-page">
      {/* Nav */}
      <header className="glass-strong border-b border-white/[0.06] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-[#F7941D] flex items-center justify-center glow-orange">
              <span className="text-white text-sm font-bold">E</span>
            </div>
            <span className="text-lg font-semibold text-white/90 tracking-tight">Eamico</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="https://eamico.com" target="_blank" rel="noopener noreferrer" className="text-sm text-white/40 hover:text-white/70 transition-colors hidden sm:inline">eamico.com</a>
            <button onClick={() => navigate("/login")} className="bg-[#F7941D] hover:bg-[#ff9f2e] text-black font-semibold rounded-full px-6 py-2.5 text-sm transition-all duration-300 hover:shadow-[0_0_20px_rgba(247,148,29,0.3)]" data-testid="landing-apply-button">
              Apply Now <ArrowRight className="w-4 h-4 ml-1.5 inline" />
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-24 pb-20">
        <div className="max-w-3xl animate-fade-in-up">
          <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-2 mb-8">
            <DollarSign className="w-4 h-4 text-[#F7941D]" />
            <span className="text-sm text-[#F7941D] font-medium">$0.50/signup + $25 every 100</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight leading-[1.1] mb-6">
            Become an Eamico{" "}
            <span className="text-[#F7941D] text-glow">Ambassador</span>
          </h1>
          <p className="text-lg text-white/50 leading-relaxed max-w-2xl mb-10">
            Help developers discover Eamico Studio — the cloud IDE that replaces their entire dev stack. Earn $0.50 for every waitlist signup, plus massive milestone bonuses.
          </p>
          <div className="flex items-center gap-4 flex-wrap">
            <button onClick={() => navigate("/login")} className="bg-[#F7941D] hover:bg-[#ff9f2e] text-black font-semibold rounded-full px-8 py-3.5 text-base transition-all duration-300 hover:shadow-[0_0_30px_rgba(247,148,29,0.3)]" data-testid="hero-apply-button">
              Apply as Ambassador
            </button>
            <button onClick={() => navigate("/login")} className="glass glass-hover rounded-full px-8 py-3.5 text-base text-white/60 hover:text-white/90 font-medium">
              Sign In
            </button>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-y border-white/[0.06] py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-semibold text-white/90 tracking-tight mb-12">How it works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 stagger">
            {[
              { icon: Users, num: "01", title: "Apply & get your link", desc: "Sign up as an ambassador and receive your unique referral link." },
              { icon: Zap, num: "02", title: "Share with developers", desc: "Share your link on social media, communities, and with developer friends. Each signup earns you $0.50." },
              { icon: Trophy, num: "03", title: "Hit milestones, earn big", desc: "Unlock massive milestone bonuses — from $100 at Bronze to $100,000 at Diamond." },
            ].map(({ icon: Icon, num, title, desc }) => (
              <div key={num} className="glass glass-hover rounded-2xl p-6 animate-fade-in-up">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs font-mono text-white/25">{num}</span>
                  <div className="w-9 h-9 rounded-xl bg-[#F7941D]/10 flex items-center justify-center">
                    <Icon className="w-[18px] h-[18px] text-[#F7941D]" strokeWidth={1.5} />
                  </div>
                </div>
                <h3 className="font-semibold text-white/85 mb-2">{title}</h3>
                <p className="text-sm text-white/40 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commission Structure */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-semibold text-white/90 tracking-tight mb-3">Commission structure</h2>
          <p className="text-white/40 mb-12 max-w-xl">Simple, transparent earnings that grow with you.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            <div className="glass glass-hover rounded-2xl p-8 text-center">
              <div className="w-14 h-14 rounded-2xl bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                <DollarSign className="w-7 h-7 text-green-400" />
              </div>
              <div className="font-mono text-4xl font-bold text-green-400 mb-2">$0.50</div>
              <div className="text-white/50">per waitlist signup</div>
              <p className="text-xs text-white/25 mt-2">Earned instantly for every person who joins through your link</p>
            </div>
            <div className="glass glass-hover rounded-2xl p-8 text-center">
              <div className="w-14 h-14 rounded-2xl bg-[#F7941D]/10 flex items-center justify-center mx-auto mb-4">
                <Trophy className="w-7 h-7 text-[#F7941D]" />
              </div>
              <div className="font-mono text-4xl font-bold text-[#F7941D] mb-2">$25 <span className="text-lg text-white/30">CAD</span></div>
              <div className="text-white/50">bonus every 100 signups</div>
              <p className="text-xs text-white/25 mt-2">Hit 100, get $25. Hit 200, get another $25. It stacks forever.</p>
            </div>
          </div>

          <div className="glass rounded-2xl p-6 mb-6">
            <p className="text-sm text-white/50 mb-3 font-medium">Example earnings</p>
            <div className="grid grid-cols-4 gap-4 text-center">
              {[100, 500, 1000, 5000].map(n => (
                <div key={n} className="glass rounded-xl p-3">
                  <div className="text-xs text-white/30 mb-1">{n.toLocaleString()} signups</div>
                  <div className="font-mono text-lg font-bold text-[#F7941D]">${(n * 0.5 + Math.floor(n / 100) * 25).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="glass rounded-2xl p-6 flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-[#F7941D]/15 flex items-center justify-center shrink-0">
              <Gift className="w-5 h-5 text-[#F7941D]" />
            </div>
            <div>
              <p className="font-semibold text-white/85">Plus: Every person who signs up gets a $50 credit</p>
              <p className="text-sm text-white/40 mt-1">When Eamico Studio launches in Beta on June 1st, 2026, each waitlist member receives $50 as a credit to their account.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="border-y border-white/[0.06] py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-semibold text-white/90 tracking-tight mb-12">Ambassador benefits</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "Unique referral link", desc: "Get your own referral page branded with Eamico's design." },
              { title: "Real-time dashboard", desc: "Track signups, earnings, and milestone progress in your portal." },
              { title: "Escalating bonuses", desc: "The more you refer, the bigger the bonuses. Diamond tier pays $100,000." },
              { title: "Marketing assets", desc: "Access logos, banners, social copy, and templates." },
              { title: "Customizable page", desc: "Personalize your referral page with a custom message." },
              { title: "Leaderboard", desc: "Compete with other ambassadors and climb the ranks." },
            ].map(({ title, desc }) => (
              <div key={title} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#F7941D] shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-white/80 text-sm">{title}</p>
                  <p className="text-xs text-white/35 mt-0.5">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-semibold text-white/90 tracking-tight mb-4">
            Ready to start earning?
          </h2>
          <p className="text-white/40 text-lg mb-10 max-w-lg mx-auto">
            Join the Eamico Ambassador Program and help developers discover the future of cloud development.
          </p>
          <button onClick={() => navigate("/login")} className="bg-[#F7941D] hover:bg-[#ff9f2e] text-black font-semibold rounded-full px-10 py-3.5 text-base transition-all duration-300 hover:shadow-[0_0_30px_rgba(247,148,29,0.3)] animate-pulse-glow" data-testid="cta-apply-button">
            Apply Now <ArrowRight className="w-4 h-4 ml-2 inline" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/[0.06] py-6">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
          <p className="text-xs text-white/25">Eamico Inc. &middot; Write, ship, and scale.</p>
          <div className="flex gap-4">
            <a href="https://eamico.com/about" target="_blank" rel="noopener noreferrer" className="text-xs text-white/25 hover:text-white/50 transition-colors">About</a>
            <a href="https://eamico.com/studio" target="_blank" rel="noopener noreferrer" className="text-xs text-white/25 hover:text-white/50 transition-colors">Studio</a>
            <a href="https://eamico.com/pricing" target="_blank" rel="noopener noreferrer" className="text-xs text-white/25 hover:text-white/50 transition-colors">Pricing</a>
            <a href="https://eamico.com/blog" target="_blank" rel="noopener noreferrer" className="text-xs text-white/25 hover:text-white/50 transition-colors">Blog</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
